import { memo, useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useParams } from 'react-router-dom';

import { useAppSelector } from 'redux/hooks';

import { budgetDataService } from 'services/budget.service';

import { BucketContext, BudgetContext, S3Item, useBudgetProduct } from 'contexts';

import { getApiErrorMessage } from 'helpers/getApiErrorMessage';

import { Table } from 'components/Table';

import * as S from './SubProduct.styles';
import { useColumns } from './columns.data';
import { useDebouncedQuantityUpdate } from './useDebouncedQuantityUpdate';

type SubProductProps = {
  productId: string;
  colorId: string;
};

function SubProductRow({ productId, colorId }: SubProductProps): React.ReactElement {
  const { getUrl } = useContext(BucketContext);
  const {
    budgetStatus,
    date,
    params,
    refreshHeader,
    isEndDatePassed,
    updateColorQuantity,
    incrementQuantityUpdate,
    decrementQuantityUpdate,
  } = useContext(BudgetContext);

  const queryParams = useParams();

  const [image, imageSet] = useState<string>('');

  const { register } = useForm();

  const { zone, season, marketLine } = useAppSelector((state) => state.settings);

  // Get product and color data from context
  // Subscribes to this product only: other products' saves don't re-render this row
  const product = useBudgetProduct(productId);
  const colorData = product?.colors.find((c) => c.id === colorId);

  // Destructure color data
  const {
    imageName = '',
    itemCode = '',
    mainFabricCode = '',
    mainFabricDesc = '',
    styleCodeAndStyleDesc = '',
    prevSold = 0,
    currSold = 0,
    currQuantity = 0,
    itemColorCode = '',
    id = colorId,
  } = colorData || {};

  // Stable API update handler
  const handleApiUpdate = useCallback(
    async (prodId: string, colorItemId: string, quantity: string) => {
      const normalizedQuantityStr = quantity.trim() === '' ? '0' : quantity;
      const normalizedQuantity = Number(normalizedQuantityStr) || 0;

      if (normalizedQuantity < 0) {
        toast.error('Quantity cannot be negative');
        return;
      }

      const updateParams = {
        budgetNum: queryParams.id!,
        season,
        marketingLine: marketLine,
        quantity: normalizedQuantityStr,
        productId: colorItemId,
        genderCode: params?.genderCode,
        type: params?.type || '',
      };

      try {
        await budgetDataService.updateQuantity(zone, updateParams);
        // Update state via context - atomic operation, no race conditions
        updateColorQuantity(prodId, colorItemId, normalizedQuantity);
        refreshHeader();
        toast.success('Budget updated');
      } catch (error) {
        // e.g. "Budget 20 has been sent and can no longer be modified." / "... is expired."
        toast.error(
          getApiErrorMessage((error as { response?: unknown })?.response, 'Budget is expired')
        );
        throw error; // Re-throw to let debounce hook handle it
      }
    },
    [
      queryParams.id,
      season,
      marketLine,
      params?.genderCode,
      zone,
      updateColorQuantity,
      refreshHeader,
    ]
  );

  // Use debounced update to prevent race conditions
  const { scheduleUpdate, isUpdating } = useDebouncedQuantityUpdate(
    handleApiUpdate,
    100,
    incrementQuantityUpdate,
    decrementQuantityUpdate
  );

  // Wrap scheduleUpdate with productId context
  const handleQuantityUpdate = useCallback(
    (colorItemId: string, quantity: string) => {
      const normalizedNew = Number(quantity) || 0;
      // Only schedule update if value actually changed
      if (normalizedNew !== currQuantity) {
        scheduleUpdate(productId, colorItemId, quantity);
      }
    },
    [scheduleUpdate, productId, currQuantity]
  );

  const columns = useColumns(
    handleQuantityUpdate,
    budgetStatus,
    date,
    product,
    register,
    isEndDatePassed,
    isUpdating
  );
  const isSelectedColor = product?.mainProduct.itemColorCode === itemColorCode;

  const tableData = {
    itemCode,
    mainFabricCode,
    mainFabricDesc,
    styleCodeAndStyleDesc,
    prevSold,
    currSold,
    currQuantity: currQuantity != null ? currQuantity : 0,
    itemColorCode: isSelectedColor ? `${itemColorCode} *` : itemColorCode,
    id,
  };

  useEffect(() => {
    if (!imageName) return;

    getUrl(`catalogue/products${imageName}`).then((res: S3Item) => {
      imageSet(res.url);
    });
  }, [imageName]);

  return (
    <S.Container>
      <S.ImageContainer>
        <S.Img source={image || ''} alt="item pic" />
      </S.ImageContainer>

      <Table motive="budgetSecondary" columns={columns} data={[tableData]} />
    </S.Container>
  );
}

export const SubProduct = memo(SubProductRow);
